import api from '@/api';

/**
 * Fetches all paginated results from an API endpoint
 * @param {string} endpoint - The API endpoint to call
 * @param {object} params - The query parameters for the API call
 * @param {number} limit - Number of items per page (default: 100)
 * @returns {Promise<Array>} - Array of all results across all pages
 */
export async function fetchAllPaginated(endpoint, params = {}, limit = 100) {
  const allResults = [];
  let from = 0;
  let hasMore = true;

  let totalCount = 0;

  while (hasMore) {
    const response = await api.get(endpoint, {
      params: {
        ...params,
        limit,
        from
      }
    });

    const data = response.data;
    const results = data.meals || data.results || data.data || [];
    totalCount = data.count || data.total || results.length;

    allResults.push(...results);

    // Check if we have more pages to fetch
    hasMore = allResults.length < totalCount;
    from += limit;
  }

  // Sanity check: verify count matches actual results
  if (totalCount > 0 && allResults.length !== totalCount) {
    console.warn(
      `API count mismatch for ${endpoint}: expected ${totalCount} records, got ${allResults.length} records`
    );
  }

  return allResults;
}

/**
 * Fetches meals within a date range with pagination support
 * @param {string} startDate - Start date in ISO format
 * @param {string} endDate - End date in ISO format
 * @param {object} additionalParams - Additional query parameters
 * @returns {Promise<Array>} - Array of all meals in the date range
 */
export async function fetchMealsInDateRange(startDate, endDate, additionalParams = {}) {
  const params = {
    where: {
      date: {
        gte: startDate,
        lte: endDate
      },
      ...additionalParams.where
    },
    ...additionalParams
  };

  return await fetchAllPaginated('v2/meal/', params, 100);
}